import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import {
	fetchAllContractorWOUsersForSuppliers,
	fetchAllUsersForBuyers,
	fetchAllUsersForSuppliers,
} from '../../thunks/work_orders_subscribers_thunks';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { imageMimeTypes, imageUploadValidation } from '../../utils/ImageUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import './index.less';
import ChatUploader from './ChatUploader';
import { getContractorOrWorkOrderId } from '../../utils/WorkOrderUtils';
import { formatAndSortUserContacts } from '../../utils/DataFormatterUtils';
import VoiceNoteRecorder from './VoiceNoteRecorder';

const videoMimeTypes = [
	'video/3gpp', //.3gp
	'video/mp4', //.mp4, .m4a, .m4p, .m4b, .m4r, .m4v
	'video/mpeg', //.m1v
	'video/ogg', //.ogg
	'video/quicktime', //.mov, .qt
	'video/webm', //.webm
	'video/x-m4v', //.m4v
	'video/ms-asf', //.asf, .wma, .wmv
	'video/x-ms-wmv', //.wmv
	'video/x-msvideo', //.avi
];

const ChatInput = ({
	handlePhotoUploadChange,
	handleStartVideoCall,
	fetchUsers,
	fetchMentionUsers,
	workOrder,
	currentUser,
	token,
	handleVideoUploadChange,
	onChange,
	value,
	fetchSupplierUsers,
	handleSubmit,
	photoFileList = [],
	videoFileList = [],
	showMultiMediaOptions = true,
	onTyping = () => {},
	typingDetails,
	onSendAudio, // optional callback(blob: Blob, file: File)
	customUploadRequest,
	renderAboveInput, // optional content rendered ABOVE the input row, width/offset aligned to the textarea box
}) => {
	const userType = currentUser.userType;

	const workOrderId = useMemo(() => getContractorOrWorkOrderId(workOrder), [workOrder]);
	const loadUsers = useCallback(
		(key: string, cb) => {
			// WorkAround: MentionsInput calls this funtion everytime. if space count is more than 1, it will not call the api.
			// https://github.com/signavio/react-mentions/issues/247#issuecomment-516529736
			const count = key.split(' ').length - 1;
			if (count < 2) {
				// When the caller supplies its own mention fetcher (e.g. walkthrough notes use a
				// walkthrough-scoped subscriber endpoint), use it instead of the work-order thunks.
				const fetchPromise = fetchMentionUsers
					? fetchMentionUsers(key)
					: currentUser.userType === ROLE_TYPES.SUPPLIER
					? fetchSupplierUsers(key, workOrderId)
					: fetchUsers(key, workOrderId);
				fetchPromise.then((data) => cb(formatAndSortUserContacts(data)));
			} else {
				cb([]);
			}
		},
		[currentUser.userType, fetchSupplierUsers, fetchUsers, fetchMentionUsers, workOrderId]
	);

	const debounceloadUsers = useCallback(debounce(loadUsers, 250), [loadUsers]);

	const onSearch = useCallback(
		function (search: string, cb) {
			debounceloadUsers(search, cb);
		},
		[debounceloadUsers]
	);

	function beforeVideoUpload(file) {
		const isVideo = videoMimeTypes.indexOf(file.type) !== -1;
		if (!isVideo) {
			message.error("This file either isn't a video or is not in a file type we support.");
		}
		return isVideo;
	}

	// The referenced-task banner renders ABOVE the input row but must line up with the textarea box, which
	// is inset by the (variable-width) media-icon block on its left. We measure the textarea box and the
	// outer container and mirror the box's left offset + width onto the banner so they always align, even
	// as the icon block or viewport width changes.
	const containerRef = useRef<HTMLDivElement>(null);
	const inputBoxRef = useRef<HTMLDivElement>(null);
	const [bannerBox, setBannerBox] = useState<{ left: number; width: number } | null>(null);

	useLayoutEffect(() => {
		if (!renderAboveInput) return undefined;
		let frame = 0;
		const measure = () => {
			const container = containerRef.current;
			const inputBox = inputBoxRef.current;
			if (!container || !inputBox) return;
			const containerRect = container.getBoundingClientRect();
			const inputRect = inputBox.getBoundingClientRect();
			const left = Math.round(inputRect.left - containerRect.left);
			const width = Math.round(inputRect.width);
			setBannerBox((prev) =>
				prev && prev.left === left && prev.width === width ? prev : { left, width }
			);
		};
		measure();
		const resizeObserver = new ResizeObserver(() => {
			window.cancelAnimationFrame(frame);
			frame = window.requestAnimationFrame(measure);
		});
		if (containerRef.current) resizeObserver.observe(containerRef.current);
		if (inputBoxRef.current) resizeObserver.observe(inputBoxRef.current);
		return () => {
			window.cancelAnimationFrame(frame);
			resizeObserver.disconnect();
		};
	}, [renderAboveInput]);

	return (
		<div ref={containerRef}>
			{renderAboveInput ? (
				<div
					className="mb-2"
					style={bannerBox ? { marginLeft: bannerBox.left, width: bannerBox.width } : undefined}
				>
					{renderAboveInput()}
				</div>
			) : null}
			<div className={`mx-4 flex flex-row `}>
				<div className="flex h-[40px] flex-row items-baseline bg-zinc-50  px-3 pb-2">
					{showMultiMediaOptions ? (
						<>
							{handleStartVideoCall ? (
								<a
									href={`https://live.useopenwrench.com/?roomName=${nullSafeGet(
										'facility.buyerCompanyId',
										currentUser
									)}-${getContractorOrWorkOrderId(
										workOrder
									)}&authToken=${token}&userType=${userType}`}
									target="_blank"
									rel="noreferrer"
								>
									<Tooltip title="Start video call">
										<Button
											onClick={handleStartVideoCall}
											shape="circle"
											style={{ border: 'none', marginRight: 4 }}
										>
											<i style={{ fontSize: 20 }} className="icons8-font icons8-video-call"></i>
										</Button>
									</Tooltip>
								</a>
							) : null}

							<ChatUploader
								name="file"
								multiple={true}
								showUploadList={false}
								beforeUpload={imageUploadValidation}
								onChange={handlePhotoUploadChange}
								id={workOrderId}
								accept={imageMimeTypes}
								fileList={photoFileList}
								customUploadRequest={customUploadRequest}
							>
								<Tooltip title="Upload photo">
									<Button style={{ border: 'none', marginRight: 4 }} shape="circle">
										<i style={{ fontSize: 20 }} className="icons8-font icons8-compact-camera"></i>
									</Button>
								</Tooltip>
							</ChatUploader>
							<ChatUploader
								name="file"
								showUploadList={false}
								beforeUpload={beforeVideoUpload}
								onChange={handleVideoUploadChange}
								accept={videoMimeTypes}
								id={workOrderId}
								fileList={videoFileList}
								customUploadRequest={customUploadRequest}
							>
								<Tooltip title="Upload video">
									<Button style={{ border: 'none' }} shape="circle">
										<i style={{ fontSize: 20 }} className="icons8-font icons8-video"></i>
									</Button>
								</Tooltip>
							</ChatUploader>
						</>
					) : null}
				</div>
				<VoiceNoteRecorder
					value={value}
					onChange={onChange}
					onTyping={onTyping}
					handleSubmit={handleSubmit}
					onSearch={onSearch}
					onSendAudio={onSendAudio}
					typingDetails={typingDetails}
					inputContainerRef={inputBoxRef}
				/>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => ({
	searched_subscribers: state.searched_subscribers,
	token: state.session.token,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchUsers: (search, woId, params) => dispatch(fetchAllUsersForBuyers(search, woId, params)()),
	fetchSupplierUsers: (search, woId, params) =>
		dispatch(
			nullSafeGet('workOrder.contractorWo', ownProps)
				? fetchAllContractorWOUsersForSuppliers(
						search,
						nullSafeGet('workOrder.contractorWo.id', ownProps),
						params
				  )()
				: fetchAllUsersForSuppliers(search, woId, params)()
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChatInput as any);
